import type { TFunction } from 'i18next';

export type TargetingLabels = Record<string, object | string>;

export const useTargetingLabels = (t: TFunction): TargetingLabels => {
  return {
    // Keys below come from the forecasting `not_included_fields` payload
    // (`TargetingAppInventory`), NOT from the legacy `deal.package.targeting.app_group`
    // options path. `app_inventory_inter_dimension_operator` is intentionally NOT mapped —
    // it is an internal operator, not a user-facing filter.
    app_inventory: {
      app_bundle_id: t('App Bundle ID'),
      appgroup: t('App Name'),
    },
    audience: {
      dmp_segments: t('3rd Party Data'),
      liveintent: t('LiveIntent'),
      openaudience_custom: t('OpenXSelect Audience'),
    },
    content: {
      account: t('Publisher IDs'),
      adunit_size: t('Ad Unit Size'),
      app_bundle_id: t('App Bundle ID'),
      excludes: {
        account: t('Inventory'),
        adunit: t('Inventory'),
        instance: t('Inventory'),
        network: t('Inventory'),
        page: t('Inventory'),
        section: t('Inventory'),
        site: t('Inventory'),
      },
      includes: {
        account: t('Inventory'),
        adunit: t('Inventory'),
        instance: t('Inventory'),
        network: t('Inventory'),
        page: t('Inventory'),
        section: t('Inventory'),
        site: t('Inventory'),
      },
      instance: t('Instance Hash'),
      keywords: t('Keywords'),
      page_url: t('Page URL'),
    },
    contextual_segment: t('Contextual Segments'),
    custom: t('Custom Variables'),
    domain: {
      categories: t('Category'),
      categories_iab_v2: t('Category'),
    },
    exclude_non_direct: t('Exclude Indirect Inventory'),
    geographic: {
      circles: t('Location Radius'),
      circles_v2: t('Location Radius'),
      excludes: {
        city: t('City'),
        continent: t('Continent'),
        country: t('Country'),
        dma: t('DMA'),
        msa: t('MSA'),
        postal_code: t('Postal Code'),
        region: t('Region'),
        state: t('State'),
      },
      includes: {
        city: t('City'),
        continent: t('Continent'),
        country: t('Country'),
        dma: t('DMA'),
        msa: t('MSA'),
        postal_code: t('Postal Code'),
        region: t('Region'),
        state: t('State'),
      },
    },
    include_open_bidding_inventory: t('Include Google Open Bidding Inventory'),
    instream_content: {
      channel: {
        name: t('Channel'),
      },
      contentrating: {
        allow: t('Rating'),
        block: t('Rating'),
      },
      episode: t('Episodes'),
      genre: {
        allow: t('Genre'),
        block: t('Genre'),
      },
      language: t('Language'),
      livestream: t('Livestream'),
      network: {
        name: t('Network'),
      },
      series: t('Series'),
    },
    inventory_format: {
      ad_placement: t('Format'),
      distribution_channel: t('Channel'),
    },
    metacategory: {
      exclude_mfa: t('Exclude MFA Inventory'),
      excludes: t('Additional OpenX Categories'),
      includes: t('Additional OpenX Categories'),
      keywords: {
        excludes: t('Keywords'),
        includes: t('Keywords'),
      },
    },
    technographic: {
      api_framework: t('API Frameworks'),
      browser: t('Browser'),
      device_type: t('Device'),
      language: t('Language'),
      os: t('OS'),
    },
    video: {
      adunit_max_duration: t('Maximum Ad Duration'),
      adunit_max_duration_range: {
        end: t('Maximum Ad Duration End'),
        start: t('Maximum Ad Duration Start'),
      },
      rewarded_video: t('Rewarded Video'),
      screen_location: t('In-stream Video Position'),
      skip: t('Video Skippability'),
      video_format: t('Presentation Formats'),
      video_plcmt: t('Placement'),
    },
    vtr: {
      vtr_score: t('View Through Rate'),
    },
  };
};
